import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';

interface FrameControlsProps {
  onSkipFrames: (frames: number) => void;
  disabled: boolean;
}

const frameControls = [
  { frames: -10, label: '-10' },
  { frames: -5, label: '-5' },
  { frames: -1, label: '-1' },
  { frames: 1, label: '+1' },
  { frames: 5, label: '+5' },
  { frames: 10, label: '+10' },
];

const FrameControls: React.FC<FrameControlsProps> = ({ onSkipFrames, disabled }) => {
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled) return;
      
      // Prevent default behavior for arrow keys
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
      }
      
      switch (e.key) {
        case 'ArrowLeft':
          onSkipFrames(e.shiftKey ? -10 : -1);
          break;
        case 'ArrowRight':
          onSkipFrames(e.shiftKey ? 10 : 1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSkipFrames, disabled]);

  return (
    <div className="flex flex-wrap justify-center gap-2">
      {frameControls.map(({ frames, label }) => (
        <button
          key={frames}
          onClick={() => onSkipFrames(frames)}
          disabled={disabled}
          className={cn(
            "px-3 py-2 text-sm font-medium rounded-md transition-all duration-150 touch-manipulation min-h-[36px] min-w-[36px] select-none",
            frames < 0 
              ? "bg-red-600 text-white hover:bg-red-700 active:bg-red-800" 
              : "bg-green-600 text-white hover:bg-green-700 active:bg-green-800",
            disabled && "opacity-50 cursor-not-allowed",
            "transform active:scale-95"
          )}
          aria-label={`Skip ${Math.abs(frames)} frame${Math.abs(frames) > 1 ? 's' : ''} ${frames < 0 ? 'backward' : 'forward'}`}
        >
          {frames < 0 ? <ChevronLeft className="w-4 h-4 inline-block mr-1" /> : null}
          {label}
          {frames > 0 ? <ChevronRight className="w-4 h-4 inline-block ml-1" /> : null}
        </button>
      ))}
    </div>
  );
};

export default FrameControls;